import React, { useState, useEffect, useRef } from "react";

import { BriefcaseBusiness } from "lucide-react";

import EnterpriseKpisPanel from "../../../../../intelligence-briefing-library/EnterpriseKpisPanel";
import type { HawkeyeMetricsAccountsInput } from "../../../../../intelligence-briefing-library/hooks/useHawkeyeMetrics";
import { useAppDispatch, useAppSelector } from "../../../../../Store/Hooks";
import { fetchExecutiveSponsors } from "../../../../../Store/Reducers/OpsSlice";
import TempPublicDataPanel from "../../../../TempViewsRendering/views/TempPublicDataPanel";
import BirdsEyeOverview, {
  type BirdsEyeClusterSummary,
} from "../BirdsEyeOverview";
import BriefingWorkspace, {
  type BriefingParentTab,
  type BriefingWorkspaceTab,
} from "../BriefingWorkspace";
import type { CreateWatchlistModalSuccess } from "../CreateWatchlistModal";
import KpiDataAvailableToggle from "../KpiDataAvailableToggle";

interface AnyTabProps {
  accountsList: any[];
  searchQuery: string;
  highlightedAccountIds?: string[];
  onHighlightedAccountIdsChange?: (accountIds: string[]) => void;
  onSponsorChange?: (sponsorSelection: {
    type: "all" | "unassigned" | "sponsor";
    sponsorId?: string;
    sponsorName?: string;
  }) => void;
  briefingParentTab?: BriefingParentTab;
  onBriefingParentTabChange?: (tab: BriefingParentTab) => void;
  briefingSubTab?: BriefingWorkspaceTab;
  onBriefingSubTabChange?: (tab: BriefingWorkspaceTab) => void;
  onBirdsEyeClusterGroupsChange?: (groups: BirdsEyeClusterSummary[]) => void;
  onSelectedBirdsEyeAccountIdsChange?: (accountIds: string[]) => void;
  selectedBirdsEyeAccountIds?: string[];
  onSaveAsWatchlist?: (accountIds: string[]) => void;
  onSaveAccountsSuccess?: (result: CreateWatchlistModalSuccess) => void;
  showUnavailableKpiData?: boolean;
  onShowUnavailableKpiDataChange?: (show: boolean) => void;
  kpiDataCounts?: {
    available: number;
    unavailable: number;
    loading?: boolean;
  };
  onKpiDataCountsChange?: (counts: {
    available: number;
    unavailable: number;
    loading: boolean;
  }) => void;
  onAccountsWithoutKpiDataChange?: (accountIds: string[]) => void;
  showUnavailableBriefingData?: boolean;
  onShowUnavailableBriefingDataChange?: (show: boolean) => void;
  briefingDataCounts?: {
    available: number;
    unavailable: number;
    loading?: boolean;
  };
  onBriefingDataCountsChange?: (counts: {
    available: number;
    unavailable: number;
    loading: boolean;
  }) => void;
  onAccountsWithoutBriefingDataChange?: (accountIds: string[]) => void;
}

const AnyTab: React.FC<AnyTabProps> = ({
  accountsList,
  highlightedAccountIds = [],
  onHighlightedAccountIdsChange,
  onSponsorChange,
  briefingParentTab,
  onBriefingParentTabChange,
  briefingSubTab,
  onBriefingSubTabChange,
  onBirdsEyeClusterGroupsChange,
  onSelectedBirdsEyeAccountIdsChange,
  selectedBirdsEyeAccountIds = [],
  onSaveAsWatchlist,
  onSaveAccountsSuccess,
  showUnavailableKpiData = true,
  onShowUnavailableKpiDataChange,
  kpiDataCounts = { available: 0, unavailable: 0, loading: true },
  onKpiDataCountsChange,
  onAccountsWithoutKpiDataChange,
  showUnavailableBriefingData = true,
  onShowUnavailableBriefingDataChange,
  briefingDataCounts = { available: 0, unavailable: 0, loading: true },
  onBriefingDataCountsChange,
  onAccountsWithoutBriefingDataChange,
}: AnyTabProps) => {
  const dispatch = useAppDispatch();
  const {
    executiveSponsors: { data: executiveSponsorsData },
  } = useAppSelector(state => state.ops);

  const [selectedSponsor, setSelectedSponsor] = useState<string>("all");
  const [selectedSponsorUserId, setSelectedSponsorUserId] = useState<
    string | null | undefined
  >(undefined);
  const hasInitializedRef = useRef<boolean>(false);

  // Fetch sponsors on mount
  useEffect(() => {
    if (!executiveSponsorsData) {
      dispatch(fetchExecutiveSponsors({}));
    }
  }, [dispatch, executiveSponsorsData]);

  // Get sponsors list
  const sponsorsList = React.useMemo(() => {
    const allOption = {
      label: "All",
      value: "all",
      userId: undefined,
    };
    const unassignedOption = {
      label: "Unassigned",
      value: "unassigned",
      userId: null,
    };

    if (!executiveSponsorsData?.executives)
      return [allOption, unassignedOption];

    const executives = executiveSponsorsData.executives
      .map((executive: any) => ({
        label: executive.name,
        value: String(executive.id),
        userId: executive.id,
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));

    return [allOption, unassignedOption, ...executives];
  }, [executiveSponsorsData]);

  const hasUnassignedAccounts = React.useMemo(() => {
    return accountsList.some((account: any) => {
      const primary = String(account.primary_sponsors_id || "").trim();
      const secondaryIds = account.secondary_sponsors_id
        ? String(account.secondary_sponsors_id)
            .split(",")
            .map((id: string) => id.trim())
            .filter(Boolean)
        : [];
      return !primary && secondaryIds.length === 0;
    });
  }, [accountsList]);

  const hawkeyeAccounts = React.useMemo(() => {
    const source = accountsList || [];

    const isUnassigned = (account: any) => {
      const primary = String(account.primary_sponsors_id || "").trim();
      const secondaryIds = account.secondary_sponsors_id
        ? String(account.secondary_sponsors_id)
            .split(",")
            .map((id: string) => id.trim())
            .filter(Boolean)
        : [];
      return !primary && secondaryIds.length === 0;
    };

    const isSponsorSelected = (account: any, sponsorId: string) => {
      const primary = String(account.primary_sponsors_id || "").trim();
      const secondaryIds = account.secondary_sponsors_id
        ? String(account.secondary_sponsors_id)
            .split(",")
            .map((id: string) => id.trim())
            .filter(Boolean)
        : [];
      return primary === sponsorId || secondaryIds.includes(sponsorId);
    };

    let filtered = source;
    if (selectedSponsor === "unassigned") {
      filtered = source.filter(isUnassigned);
    } else if (selectedSponsor !== "all") {
      const sponsorId = String(selectedSponsorUserId ?? selectedSponsor).trim();
      filtered = sponsorId
        ? source.filter(account => isSponsorSelected(account, sponsorId))
        : [];
    }

    return filtered.map((account: any) => ({
      id: String(account.customer_id || account.id || ""),
      name: String(account.name || account.account_name || "Unknown Account"),
      city: account.city,
      country: account.country,
      region: account.region,
      latitude: account.latitude,
      longitude: account.longitude,
      primary_sponsors: account.primary_sponsors ?? null,
      primary_sponsors_id: account.primary_sponsors_id ?? null,
      secondary_sponsors: account.secondary_sponsors ?? null,
      secondary_sponsors_id: account.secondary_sponsors_id ?? null,
    }));
  }, [accountsList, selectedSponsor, selectedSponsorUserId]);

  // Set default sponsor (Unassigned) when sponsors list is available (initial mount)
  useEffect(() => {
    if (sponsorsList.length > 0 && !hasInitializedRef.current) {
      const defaultSponsor =
        sponsorsList.find((s: any) => s.value === "all") ?? sponsorsList[0];

      setSelectedSponsor(defaultSponsor.value);
      setSelectedSponsorUserId(defaultSponsor.userId);
      hasInitializedRef.current = true;

      // Notify parent of sponsor selection for left-side accounts filtering.
      if (onSponsorChange) {
        if (defaultSponsor.value === "all") {
          onSponsorChange({ type: "all" });
        } else if (defaultSponsor.value === "unassigned") {
          onSponsorChange({ type: "unassigned" });
        } else {
          onSponsorChange({
            type: "sponsor",
            sponsorId: String(defaultSponsor.userId ?? defaultSponsor.value),
            sponsorName: defaultSponsor.label,
          });
        }
      }
    }
  }, [sponsorsList, onSponsorChange]);

  useEffect(() => {
    if (!hasUnassignedAccounts && selectedSponsor === "unassigned") {
      setSelectedSponsor("all");
      setSelectedSponsorUserId(undefined);
      onSponsorChange?.({ type: "all" });
    }
  }, [hasUnassignedAccounts, selectedSponsor, onSponsorChange]);

  // Handle sponsor selection change.
  // Selection format:
  // - "all" for all accounts
  // - "unassigned" for accounts without sponsors
  // - "<userId>" for a specific executive sponsor
  const handleSponsorChange = (selectedValue: string) => {
    setSelectedSponsor(selectedValue);
    const selectedSponsorData = sponsorsList.find(
      (sponsor: any) => sponsor.value === selectedValue
    );
    const newUserId = selectedSponsorData?.userId;
    setSelectedSponsorUserId(newUserId);

    if (onSponsorChange) {
      if (selectedValue === "all") {
        onSponsorChange({ type: "all" });
      } else if (selectedValue === "unassigned") {
        onSponsorChange({ type: "unassigned" });
      } else {
        onSponsorChange({
          type: "sponsor",
          sponsorId: String(newUserId ?? selectedValue),
          sponsorName: selectedSponsorData?.label,
        });
      }
    }
  };

  const portfolioBlock =
    sponsorsList.length > 0 ? (
      <div className="inline-flex items-center gap-2">
        <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-gray-900">
          <BriefcaseBusiness className="h-4 w-4" aria-hidden />
          Portfolio
        </span>
        <select
          name="sponsor"
          value={selectedSponsor}
          onChange={e => handleSponsorChange(e.target.value)}
          className="h-10 min-w-[10rem] rounded-lg border border-[#d1d5db] bg-white px-3 pr-8 text-sm text-gray-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] outline-none transition-colors focus:border-[#974ae4] focus:ring-1 focus:ring-[#974ae4]"
          aria-label="Select executive sponsor"
        >
          {sponsorsList.map(option => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.value === "unassigned" && !hasUnassignedAccounts}
            >
              {option.label}
            </option>
          ))}
        </select>
      </div>
    ) : null;

  const metricsAccountsInput = React.useMemo<HawkeyeMetricsAccountsInput>(
    () => ({
      selectedAccounts: hawkeyeAccounts,
      allWatchlistAccounts: hawkeyeAccounts,
      watchlistId: null,
      selectedMeasurementDate: null,
      latestMeasurementDate: null,
    }),
    [hawkeyeAccounts]
  );

  const briefingPanelRef = useRef<HTMLDivElement>(null);

  return (
    <div className="w-full flex-1 min-w-0 h-full">
      <div
        ref={briefingPanelRef}
        className="main-content flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
      >
        <div className="shrink-0 px-3 pt-3 pb-1">
          <h1 className="section-title mb-0">Insights</h1>
        </div>
        <div className="min-h-0 flex-1">
          <BriefingWorkspace
            briefingParentTab={briefingParentTab}
            onBriefingParentTabChange={onBriefingParentTabChange}
            briefingSubTab={briefingSubTab}
            onBriefingSubTabChange={onBriefingSubTabChange}
            parentHeaderRight={portfolioBlock}
            headerRight={
              <KpiDataAvailableToggle
                variant="briefing"
                checked={showUnavailableBriefingData}
                onCheckedChange={checked =>
                  onShowUnavailableBriefingDataChange?.(checked)
                }
                unavailableCount={briefingDataCounts.unavailable}
                loading={briefingDataCounts.loading !== false}
              />
            }
            kpiHeaderRight={
              <KpiDataAvailableToggle
                checked={showUnavailableKpiData}
                onCheckedChange={checked =>
                  onShowUnavailableKpiDataChange?.(checked)
                }
                availableCount={kpiDataCounts.available}
                unavailableCount={kpiDataCounts.unavailable}
                loading={kpiDataCounts.loading !== false}
              />
            }
            tempPublicDataContent={
              <TempPublicDataPanel
                accounts={hawkeyeAccounts.map(a => ({
                  id: a.id,
                  name: a.name,
                }))}
                highlightedAccountIds={highlightedAccountIds}
                onHighlightedAccountIdsChange={onHighlightedAccountIdsChange}
                onSaveAsWatchlist={onSaveAsWatchlist}
                selectionPanelRef={briefingPanelRef}
                showUnavailableBriefingData={showUnavailableBriefingData}
                onBriefingDataCountsChange={onBriefingDataCountsChange}
                onAccountsWithoutBriefingDataChange={
                  onAccountsWithoutBriefingDataChange
                }
              />
            }
            businessKpiContent={
              <EnterpriseKpisPanel
                accounts={hawkeyeAccounts}
                highlightedAccountIds={highlightedAccountIds}
                onHighlightedAccountIdsChange={onHighlightedAccountIdsChange}
                onSaveAsWatchlist={onSaveAsWatchlist}
                showUnavailableKpiData={showUnavailableKpiData}
                onKpiDataCountsChange={onKpiDataCountsChange}
                onAccountsWithoutKpiDataChange={onAccountsWithoutKpiDataChange}
                selectionPanelRef={briefingPanelRef}
              />
            }
            birdsEyeContent={
              <BirdsEyeOverview
                metricsAccountsInput={metricsAccountsInput}
                onClusterGroupsChange={onBirdsEyeClusterGroupsChange}
                onSelectedAccountIdsChange={onSelectedBirdsEyeAccountIdsChange}
                selectedAccountIds={selectedBirdsEyeAccountIds}
                onSaveAsWatchlist={onSaveAsWatchlist}
                onSaveAccountsSuccess={onSaveAccountsSuccess}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export default AnyTab;
